import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const API_URL = '/api/jobs';

export const fetchJobs = createAsyncThunk(
  'jobs/fetchJobs',
  // v10.20.12: `view` controls the new archive filter (active | archived |
  // all). Legacy `includeArchived` still maps to include_deleted on the
  // server for backwards compat with callers that want soft-deleted rows.
  async ({ status, skip = 0, limit = 100, includeArchived = false, view, search } = {}, { rejectWithValue }) => {
    try {
      const config = {
        params: {
          status,
          skip,
          limit,
          include_archived: includeArchived || undefined,
          view: view || undefined,
          search: (search && search.trim()) || undefined,
        },
      };
      const { data } = await axios.get(`${API_URL}/`, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const restoreJob = createAsyncThunk(
  'jobs/restore',
  async (jobId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/${jobId}/restore`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchJobStats = createAsyncThunk(
  'jobs/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL}/stats`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchAvailableFiles = createAsyncThunk(
  'jobs/fetchAvailableFiles',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL}/available-files`);
      return data.files;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createJob = createAsyncThunk(
  'jobs/createJob',
  async (jobData, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const { data } = await axios.post(`${API_URL}/`, jobData, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createBatchJobs = createAsyncThunk(
  'jobs/createBatchJobs',
  async (jobData, { rejectWithValue }) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };
      const { data } = await axios.post(`${API_URL}/batch`, jobData, config);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateJobPriority = createAsyncThunk(
  'jobs/updatePriority',
  async ({ jobId, priority }, { rejectWithValue }) => {
    try {
      await axios.put(
        `${API_URL}/${jobId}/priority?priority=${priority}`,
        {}
      );
      return { jobId, priority };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const cancelJob = createAsyncThunk(
  'jobs/cancel',
  async (jobId, { rejectWithValue }) => {
    try {
      await axios.post(`${API_URL}/${jobId}/cancel`, {});
      return jobId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteJob = createAsyncThunk(
  'jobs/delete',
  async (jobId, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/${jobId}`);
      return jobId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateJobDownload = createAsyncThunk(
  'jobs/updateDownload',
  async ({ jobId, allow_download, download_enabled_from, download_enabled_until }, { rejectWithValue }) => {
    try {
      const { data } = await axios.patch(`${API_URL}/${jobId}/download`, {
        allow_download,
        download_enabled_from: download_enabled_from || null,
        download_enabled_until: download_enabled_until || null,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const restartJob = createAsyncThunk(
  'jobs/restart',
  async (jobId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/${jobId}/restart`, {});
      return { jobId, status: data.status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const jobsSlice = createSlice({
  name: 'jobs',
  initialState: {
    jobs: [],
    availableFiles: [],
    stats: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearJobsError: (state) => {
      state.error = null;
    },
    addJob: (state, action) => {
      const idx = state.jobs.findIndex(j => j.id === action.payload.id);
      if (idx !== -1) {
        state.jobs[idx] = { ...state.jobs[idx], ...action.payload };
      } else {
        state.jobs.unshift(action.payload);
      }
    },
    updateJobInList: (state, action) => {
      const index = state.jobs.findIndex(job => job.id === action.payload.id);
      if (index !== -1) {
        const merged = { ...state.jobs[index], ...action.payload };
        // Recompute ETA live for PROCESSING jobs from current progress +
        // started_at so the UI reflects actual encode rate without waiting
        // for a polled refresh. Server's eta_seconds was the seed value
        // and stays authoritative for PENDING jobs.
        if (merged.status === 'processing' && merged.started_at && merged.progress > 1) {
          const elapsed = (Date.now() - new Date(merged.started_at).getTime()) / 1000;
          if (elapsed > 0) {
            merged.eta_seconds = Math.max(0, Math.round(elapsed * (100 - merged.progress) / merged.progress));
          }
        }
        state.jobs[index] = merged;
      }
    },
    removeJob: (state, action) => {
      // Remove job from the list
      state.jobs = state.jobs.filter(job => job.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch jobs
    builder
      .addCase(fetchJobs.pending, (state) => {
        // Don't set loading to true if we already have jobs (prevents flicker)
        if (state.jobs.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        // Smart merge: Update existing jobs and add new ones without replacing the entire array
        const newJobs = action.payload;
        const existingJobIds = new Set(state.jobs.map(job => job.id));
        const newJobIds = new Set(newJobs.map(job => job.id));

        // Update existing jobs in place
        state.jobs.forEach((job, index) => {
          const newJob = newJobs.find(nj => nj.id === job.id);
          if (newJob) {
            // Update only changed properties
            state.jobs[index] = newJob;
          }
        });

        // Add new jobs that don't exist yet
        newJobs.forEach(newJob => {
          if (!existingJobIds.has(newJob.id)) {
            state.jobs.unshift(newJob);
          }
        });

        // Remove jobs that no longer exist
        state.jobs = state.jobs.filter(job => newJobIds.has(job.id));
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
    // Fetch stats
      .addCase(fetchJobStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
    // Fetch available files
      .addCase(fetchAvailableFiles.fulfilled, (state, action) => {
        state.availableFiles = action.payload;
      })
      .addCase(fetchAvailableFiles.rejected, (state, _action) => {
        state.availableFiles = [];
      })
    // Create job
      .addCase(createJob.fulfilled, (state, action) => {
        const idx = state.jobs.findIndex(j => j.id === action.payload.id);
        if (idx !== -1) {
          state.jobs[idx] = { ...state.jobs[idx], ...action.payload };
        } else {
          state.jobs.unshift(action.payload);
        }
      })
    // Create batch jobs
      .addCase(createBatchJobs.fulfilled, (state, action) => {
        action.payload.forEach(job => {
          const idx = state.jobs.findIndex(j => j.id === job.id);
          if (idx !== -1) {
            state.jobs[idx] = { ...state.jobs[idx], ...job };
          } else {
            state.jobs.unshift(job);
          }
        });
      })
    // Update job priority
      .addCase(updateJobPriority.fulfilled, (state, action) => {
        const job = state.jobs.find(j => j.id === action.payload.jobId);
        if (job) {
          job.priority = action.payload.priority;
        }
      })
    // Update download gate (allow_download + optional window)
      .addCase(updateJobDownload.fulfilled, (state, action) => {
        const job = state.jobs.find(j => j.id === action.payload.id);
        if (job) {
          job.allow_download = action.payload.allow_download;
          job.download_enabled_from = action.payload.download_enabled_from;
          job.download_enabled_until = action.payload.download_enabled_until;
        }
      })
    // Cancel job
      .addCase(cancelJob.fulfilled, (state, action) => {
        const job = state.jobs.find(j => j.id === action.payload);
        if (job) {
          job.status = 'cancelled';
        }
      })
    // Delete job
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.jobs = state.jobs.filter(j => j.id !== action.payload);
      })
    // Restart job
      .addCase(restartJob.fulfilled, (state, action) => {
        const job = state.jobs.find(j => j.id === action.payload.jobId);
        if (job) {
          job.status = action.payload.status;
          job.progress = 0;
          job.error_message = null;
        }
      });
  },
});

export const { clearJobsError, addJob, updateJobInList, removeJob } = jobsSlice.actions;
export default jobsSlice.reducer;